import { useState, type FormEvent } from 'react';
import { KeyRound, UserRound } from 'lucide-react';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { api, ApiError } from '../../shared/api/client.js';
import { useProjects } from '../../shared/api/queries.js';
import { useSession } from '../../shared/auth/session.js';
import { passwordProblem, PASSWORD_RULE } from './password-messages.js';
import './login.css';
import './profile.css';

const ROLE_LABELS = { central_access: 'Accès central', platform_access: 'Accès plateforme' } as const;

/** The signed-in account: who it is, what it covers, and its password (functional §9.2). */
export function ProfilePage() {
  const { account } = useSession();
  const { data: projects = [] } = useProjects();
  if (!account) return null;

  const perimeter =
    account.role === 'central_access'
      ? 'Tous les projets'
      : projects
          .filter((project) => account.projectScope.includes(project.id))
          .map((project) => project.name)
          .join(', ') || '—';

  return (
    <>
      <PageHeader title="Mon profil" description="Votre compte, son périmètre et votre mot de passe." />
      <div className="profile">
        <Card title="Compte" icon={<UserRound size={15} />}>
          <dl className="profile__facts">
            <dt>Nom</dt>
            <dd>{account.displayName}</dd>
            <dt>Adresse e-mail</dt>
            <dd>{account.email}</dd>
            <dt>Profil</dt>
            <dd>
              <Badge tone={account.role === 'central_access' ? 'brand' : 'neutral'}>
                {ROLE_LABELS[account.role]}
              </Badge>
            </dd>
            <dt>Périmètre</dt>
            <dd>{perimeter}</dd>
            <dt>Inactivité</dt>
            <dd>La session se ferme après {account.sessionIdleMinutes} minutes sans action.</dd>
          </dl>
        </Card>
        <ChangePasswordCard />
      </div>
    </>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setMessage({ tone: 'error', text: 'Les deux saisies du nouveau mot de passe ne sont pas identiques.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api('/auth/me/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrent('');
      setNew('');
      setConfirmation('');
      setMessage({ tone: 'success', text: 'Mot de passe changé. Vos autres sessions ont été fermées.' });
    } catch (cause) {
      setMessage({
        tone: 'error',
        text:
          cause instanceof ApiError && cause.code === 'CURRENT_PASSWORD_MISMATCH'
            ? "Le mot de passe actuel n'est pas le bon."
            : passwordProblem(cause),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Changer de mot de passe" icon={<KeyRound size={15} />}>
      <form onSubmit={submit} className="profile__form">
        <label className="login__field">
          <span>Mot de passe actuel</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        <label className="login__field">
          <span>Nouveau mot de passe</span>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
            required
            autoComplete="new-password"
          />
        </label>
        <label className="login__field">
          <span>Confirmation</span>
          <input
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            required
            autoComplete="new-password"
          />
        </label>
        <p className="login__hint login__hint--rule">{PASSWORD_RULE}</p>
        {message && (
          <p className={message.tone === 'error' ? 'login__error' : 'login__success'} role="status">
            {message.text}
          </p>
        )}
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? 'Enregistrement…' : 'Changer le mot de passe'}
        </Button>
      </form>
    </Card>
  );
}
